import { PaletteColorPicker } from "@/components/cms/theme/PaletteColorPicker";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { CmsColor, CmsPageTextColorMode } from "@/lib/cms/cms-theme.constants";
import { useTranslation } from "react-i18next";

type Props = {
  colors: CmsColor[];
  pageTextColorMode: CmsPageTextColorMode;
  pageTextColorId: string | null | undefined;
  themePreviewHex?: string;
  themePreviewLabel?: string;
  onModeChange: (mode: CmsPageTextColorMode) => void;
  onColorIdChange: (colorId: string | null) => void;
  onColorsChange?: (colors: CmsColor[]) => void;
};

export function PageTextColorField({
  colors,
  pageTextColorMode,
  pageTextColorId,
  themePreviewHex,
  themePreviewLabel,
  onModeChange,
  onColorIdChange,
  onColorsChange,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-slate-200 bg-transparent p-4 space-y-4">
      <h4 className="text-sm font-semibold text-slate-800">
        {t("cms.page.settings.textColorTitle")}
      </h4>
      <RadioGroup
        value={pageTextColorMode}
        onValueChange={(v) => onModeChange(v as CmsPageTextColorMode)}
        className="flex flex-col gap-2"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="AUTO" id="page-text-auto" />
          <Label htmlFor="page-text-auto" className="font-normal cursor-pointer">
            {t("cms.page.settings.textColor.auto")}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="THEME_TEXT" id="page-text-theme" />
          <div className="flex min-w-0 flex-col gap-1">
            <Label
              htmlFor="page-text-theme"
              className="font-normal cursor-pointer"
            >
              {t("cms.page.settings.textColor.themeText")}
            </Label>
            {themePreviewHex && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span
                  className="h-5 w-5 shrink-0 rounded border border-slate-200"
                  style={{ backgroundColor: themePreviewHex }}
                  aria-hidden
                />
                <span className="truncate">
                  {themePreviewLabel ??
                    t("cms.page.settings.textColor.preview", {
                      hex: themePreviewHex,
                    })}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="CUSTOM" id="page-text-custom" />
          <Label
            htmlFor="page-text-custom"
            className="font-normal cursor-pointer"
          >
            {t("cms.page.settings.textColor.custom")}
          </Label>
        </div>
      </RadioGroup>
      {pageTextColorMode === "CUSTOM" && (
        <PaletteColorPicker
          label={t("cms.page.settings.textColor.color")}
          colors={colors}
          value={pageTextColorId}
          onChange={onColorIdChange}
          onColorsChange={onColorsChange}
        />
      )}
    </div>
  );
}
